// Wrapper pour le style immersif des pages Espace Pro
// Header + Navigation + Hero unifiés avec fond bleu nuit premium
// À utiliser sur toutes les pages /pro/*

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wand2 } from 'lucide-react';
import { motion } from 'framer-motion';
import FloatingAIAssistant from '../ai/FloatingAIAssistant';
import CockpitFAB from './CockpitFAB';

// Bouton flottant Cockpit Pro - Rectangulaire avec baguette magique
const FloatingCockpitProButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : 100 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        bottom: 24,
        right: 16,
        zIndex: 9990,
      }}
    >
      <Link
        to="/cockpit-pro"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 20px',
          background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
          color: '#fff',
          borderRadius: 14,
          boxShadow: '0 8px 28px rgba(249, 115, 22, 0.45), 0 0 0 2px rgba(249, 115, 22, 0.15)',
          textDecoration: 'none',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 700,
          fontSize: 14,
          transition: 'all 0.25s ease',
          border: '2px solid rgba(255, 255, 255, 0.25)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
          e.currentTarget.style.boxShadow = '0 12px 35px rgba(249, 115, 22, 0.55), 0 0 0 3px rgba(249, 115, 22, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 28px rgba(249, 115, 22, 0.45), 0 0 0 2px rgba(249, 115, 22, 0.15)';
        }}
      >
        <Wand2 size={20} strokeWidth={2.5} />
        <span>Cockpit Pro</span>
      </Link>
    </motion.div>
  );
};

// Styles CSS pour la navigation immersive premium
const ImmersiveStyles = () => (
  <style>{`
    /* ═══════════════════════════════════════════════════════════════════════
       ESPACE PRO : Navigation Premium — Harmonisation des 3 niveaux
    ═══════════════════════════════════════════════════════════════════════ */
    
    /* ══ FOND GLOBAL — Bleu nuit pour éviter les flashs blancs ══ */
    body.espace-pro-immersive {
      background: #072B57 !important;
    }
    
    /* ══ 1. HEADER SUPÉRIEUR — Dégradé premium OPAQUE ══ */
    body.espace-pro-immersive header.header-unified {
      background: linear-gradient(
        to bottom,
        rgb(11, 29, 58),
        rgb(15, 38, 70)
      ) !important;
    }
    
    /* ══ 2. MENU PRINCIPAL DESKTOP — Premium et centré OPAQUE ══ */
    body.espace-pro-immersive nav.espacepr-main-nav {
      background: linear-gradient(
        to bottom,
        rgb(18, 45, 78),
        rgb(22, 52, 88)
      ) !important;
      border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
      box-shadow: 
        inset 0 1px 0 rgba(255, 255, 255, 0.05),
        0 8px 30px rgba(0, 0, 0, 0.15) !important;
    }
    
    /* ══ GAP MENU ITEMS ══ */
    body.espace-pro-immersive nav.espacepr-main-nav > div {
      gap: 8px !important;
    }
    
    /* ═══ ITEMS MENU — Padding compact ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link {
      padding: 8px 16px !important;
    }
    
    /* ═══ 1. TRANSITION GLOBALE — Cubic-bezier Apple/Stripe ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link {
      transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1) !important;
      cursor: pointer !important;
      will-change: transform !important;
    }
    
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link svg {
      transition: all 0.2s ease !important;
      will-change: transform, opacity !important;
    }
    
    /* ═══ 2-4. ITEMS INACTIFS — État normal ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link:not(.bg-\\[\\#1E3A5F\\]) {
      color: rgba(255, 255, 255, 0.75) !important;
      font-weight: 500 !important;
      letter-spacing: 0.3px !important;
      border-radius: 12px !important;
    }
    
    /* ═══ 2-4. ITEMS INACTIFS — Hover "Léger Float" ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link:not(.bg-\\[\\#1E3A5F\\]):hover {
      color: #FFFFFF !important;
      background: rgba(255, 255, 255, 0.05) !important;
      border-radius: 8px !important;
      transform: translateY(-1px) !important;
      /* Effet lumière subtile - surface premium */
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), inset 0 0 0 1px rgba(255, 255, 255, 0.04) !important;
      transition: all 0.25s ease !important;
    }
    
    /* ═══ 5. ICÔNES INACTIVES — État normal ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link:not(.bg-\\[\\#1E3A5F\\]) svg {
      opacity: 0.7 !important;
      color: rgba(255, 255, 255, 0.7) !important;
    }
    
    /* ═══ 5. ICÔNES INACTIVES — Hover avec scale ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link:not(.bg-\\[\\#1E3A5F\\]):hover svg {
      opacity: 1 !important;
      color: #F97316 !important;
      transform: scale(1.05) !important;
    }
    
    /* ═══ 6. ITEM ACTIF — État subtil premium (moins dominant) ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link.bg-\\[\\#1E3A5F\\] {
      background: rgba(249, 115, 22, 0.12) !important;
      color: #F97316 !important;
      border: 1px solid rgba(249, 115, 22, 0.25) !important;
      border-radius: 10px !important;
      backdrop-filter: blur(6px) !important;
      -webkit-backdrop-filter: blur(6px) !important;
      box-shadow: none !important;
      font-weight: 600 !important;
      letter-spacing: 0.3px !important;
    }
    
    /* ═══ 7. ITEM ACTIF — Hover ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link.bg-\\[\\#1E3A5F\\]:hover {
      background: rgba(249, 115, 22, 0.18) !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 12px rgba(249, 115, 22, 0.15) !important;
    }
    
    /* ═══ ICÔNE ITEM ACTIF ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link.bg-\\[\\#1E3A5F\\] svg {
      color: #F97316 !important;
      opacity: 1 !important;
    }
    
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link.bg-\\[\\#1E3A5F\\]:hover svg {
      transform: scale(1.05) !important;
    }
    
    /* ═══ 8. CHEVRONS — Ultra subtil ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link .chevron-icon,
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link svg[class*="chevron"],
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link svg:last-child {
      transition: all 0.2s ease !important;
    }
    
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link:hover .chevron-icon,
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link:hover svg[class*="chevron"] {
      transform: rotate(180deg) !important;
      opacity: 0.9 !important;
    }
    
    /* ═══ FOCUS ACCESSIBILITÉ ═══ */
    body.espace-pro-immersive nav.espacepr-main-nav .espacepr-nav-link:focus-visible {
      outline: 2px solid rgba(249, 115, 22, 0.35) !important;
      outline-offset: 3px !important;
    }
    
    /* ══ 3. SOUS-MENU SECTIONS DESKTOP — OPAQUE ══ */
    body.espace-pro-immersive nav[style*="top: 130px"] {
      background: linear-gradient(
        to bottom,
        rgb(22, 52, 88),
        rgb(28, 60, 98)
      ) !important;
      border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
      box-shadow: 
        inset 0 1px 0 rgba(255, 255, 255, 0.04),
        0 4px 16px rgba(0, 0, 0, 0.12) !important;
    }
    
    /* ═══ CONTAINER SOUS-MENU — Espacement premium ═══ */
    body.espace-pro-immersive nav[style*="top: 130px"] > div {
      gap: 12px !important;
      padding-top: 10px !important;
      padding-bottom: 14px !important;
    }
    
    /* ═══ BOUTONS SOUS-MENU — Style SaaS Premium ═══ */
    body.espace-pro-immersive nav[style*="top: 130px"] button {
      transition: all 0.25s ease !important;
      cursor: pointer !important;
      will-change: transform !important;
      background: rgba(255, 255, 255, 0.08) !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      color: rgba(255, 255, 255, 0.85) !important;
      border: 1px solid rgba(255, 255, 255, 0.12) !important;
      border-radius: 10px !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    }
    
    /* ═══ BOUTONS SOUS-MENU — Hover Premium ═══ */
    body.espace-pro-immersive nav[style*="top: 130px"] button:hover {
      background: rgba(255, 255, 255, 0.12) !important;
      color: #FFFFFF !important;
      transform: translateY(-2px) !important;
      border-color: rgba(255, 255, 255, 0.18) !important;
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
    }
    
    /* ═══ ICÔNES SOUS-MENU ═══ */
    body.espace-pro-immersive nav[style*="top: 130px"] button svg {
      transition: all 0.2s ease !important;
      opacity: 0.7 !important;
      will-change: transform, opacity !important;
    }
    
    body.espace-pro-immersive nav[style*="top: 130px"] button:hover svg {
      opacity: 1 !important;
      color: #F97316 !important;
      transform: scale(1.05) !important;
    }
    
    /* ═══ BOUTON ACTIF SOUS-MENU — Gradient Premium ═══ */
    body.espace-pro-immersive nav[style*="top: 130px"] button[style*="background: rgb(249, 115, 22)"],
    body.espace-pro-immersive nav[style*="top: 130px"] button[style*="#F97316"] {
      background: linear-gradient(135deg, #F97316 0%, #FB923C 100%) !important;
      color: white !important;
      border: none !important;
      border-radius: 12px !important;
      box-shadow: 0 6px 20px rgba(249, 115, 22, 0.4) !important;
    }
    
    body.espace-pro-immersive nav[style*="top: 130px"] button[style*="background: rgb(249, 115, 22)"]:hover,
    body.espace-pro-immersive nav[style*="top: 130px"] button[style*="#F97316"]:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 8px 24px rgba(249, 115, 22, 0.5) !important;
      filter: brightness(1.05) !important;
    }
    
    body.espace-pro-immersive nav[style*="top: 130px"] button[style*="#F97316"] svg {
      color: white !important;
      opacity: 1 !important;
    }
    
    body.espace-pro-immersive nav[style*="top: 130px"] button[style*="#F97316"]:hover svg {
      transform: scale(1.05) !important;
    }
    
    /* ═══ FOCUS ACCESSIBILITÉ SOUS-MENU ═══ */
    body.espace-pro-immersive nav[style*="top: 130px"] button:focus-visible {
      outline: 2px solid rgba(249, 115, 22, 0.35) !important;
      outline-offset: 3px !important;
    }
    
    /* ══ MOBILE: Navigation sections — OPAQUE ══ */
    body.espace-pro-immersive nav[style*="position: fixed"][style*="top: 52px"] {
      background: linear-gradient(
        to bottom,
        rgb(18, 45, 78),
        rgb(22, 52, 88)
      ) !important;
      border-top: 1px solid rgba(255, 255, 255, 0.05) !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
      box-shadow: 
        inset 0 1px 0 rgba(255, 255, 255, 0.04),
        0 4px 16px rgba(0, 0, 0, 0.2) !important;
    }
    
    /* ═══ BOUTONS MOBILE — Transitions cubic-bezier ═══ */
    body.espace-pro-immersive nav[style*="top: 52px"] button {
      transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1) !important;
      cursor: pointer !important;
      will-change: transform !important;
      background: rgba(255, 255, 255, 0.06) !important;
      color: rgba(255, 255, 255, 0.75) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
      border-radius: 10px !important;
    }
    
    /* ═══ BOUTONS MOBILE — Active (touch) ═══ */
    body.espace-pro-immersive nav[style*="top: 52px"] button:active {
      background: rgba(255, 255, 255, 0.12) !important;
      color: #FFFFFF !important;
      transform: scale(0.98) !important;
    }
    
    /* ═══ ICÔNES MOBILE ═══ */
    body.espace-pro-immersive nav[style*="top: 88px"] button svg {
      transition: all 0.2s ease !important;
      color: rgba(255, 255, 255, 0.65) !important;
      opacity: 0.7 !important;
    }
    
    body.espace-pro-immersive nav[style*="top: 88px"] button:active svg {
      color: #F97316 !important;
      opacity: 1 !important;
    }
    
    /* ═══ BOUTON ACTIF MOBILE ═══ */
    body.espace-pro-immersive nav[style*="top: 88px"] button[class*="bg-[#1E3A5F]"] {
      background: linear-gradient(135deg, #F97316 0%, #E8650A 100%) !important;
      color: white !important;
      border: none !important;
      border-radius: 12px !important;
      box-shadow: 0 6px 18px rgba(249, 115, 22, 0.28) !important;
    }
    
    body.espace-pro-immersive nav[style*="top: 88px"] button[class*="bg-[#1E3A5F]"]:active {
      transform: scale(0.97) !important;
      box-shadow: 0 4px 12px rgba(249, 115, 22, 0.35) !important;
      filter: brightness(1.04) !important;
    }
    
    body.espace-pro-immersive nav[style*="top: 88px"] button[class*="bg-[#1E3A5F]"] svg {
      color: #FFFFFF !important;
      opacity: 1 !important;
    }
    
    /* ═══ FOCUS ACCESSIBILITÉ MOBILE ═══ */
    body.espace-pro-immersive nav[style*="top: 88px"] button:focus-visible {
      outline: 2px solid rgba(249, 115, 22, 0.35) !important;
      outline-offset: 3px !important;
    }
  `}</style>
);

// Hook pour activer le mode immersif
export function useImmersiveEspacePro() {
  useEffect(() => {
    document.body.classList.add('espace-pro-immersive');
    return () => {
      document.body.classList.remove('espace-pro-immersive');
    };
  }, []);
}

// Composant wrapper complet
export default function ImmersiveEspaceProWrapper({ children }) {
  useImmersiveEspacePro();
  
  return (
    <>
      <ImmersiveStyles />
      {children}
      <FloatingCockpitProButton />
      <FloatingAIAssistant />
      <CockpitFAB />
    </>
  );
}
